'use client'

import { Info, Loader2 } from '@chain-zen/ui'

import type { IELDApp } from '../../../../../../../data/queries/eld-apps'
import type { TActiveAssetType } from '../../hooks/use-eld-asset-selection'
import { AssetInputs } from './asset-inputs'
import { ELDAppItem } from './eld-app-item'
import { ELDConfirmationModal } from './eld-confirmation-modal'
import { EnableELDAppsButton } from './enable-eld-apps-button'

interface IELDAppListProps {
  apps: IELDApp[]
  confirmingAssetType?: TActiveAssetType | null
  currentTrailerNumber?: string | null
  currentTruckNumber?: string | null
  isLoadingUpdate?: boolean
  loading: boolean
  onAppSelect: (app: IELDApp) => void
  onCancelConfirmation?: () => void
  onConfirmRemove?: () => void
  onConfirmReplace?: () => void
  onTrailerInputChange?: (value: string) => void
  onTrailerRemoveClick?: () => void
  onTrailerSubmit?: () => void
  onTruckInputChange?: (value: string) => void
  onTruckRemoveClick?: () => void
  onTruckSubmit?: () => void
  pendingAction?: { type: string; value: string | null } | null
  refetchELDApps?: () => void
  showRemoveConfirmation?: boolean
  showReplaceConfirmation?: boolean
  trailerInputValue?: string
  truckInputValue?: string
}

export const ELDAppList = ({
  apps,
  confirmingAssetType,
  currentTrailerNumber,
  currentTruckNumber,
  isLoadingUpdate = false,
  loading,
  onAppSelect,
  onCancelConfirmation,
  onConfirmRemove,
  onConfirmReplace,
  onTrailerInputChange,
  onTrailerRemoveClick,
  onTrailerSubmit,
  onTruckInputChange,
  onTruckRemoveClick,
  onTruckSubmit,
  pendingAction,
  refetchELDApps,
  showRemoveConfirmation = false,
  showReplaceConfirmation = false,
  trailerInputValue = '',
  truckInputValue = ''
}: IELDAppListProps) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2.5">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading ELD apps...</p>
      </div>
    )
  }

  const isConfirming = showReplaceConfirmation || showRemoveConfirmation
  const confirmingLabel = confirmingAssetType === 'trailer' ? 'trailer' : 'truck'
  const confirmingCurrentValue = confirmingAssetType === 'trailer' ? currentTrailerNumber : currentTruckNumber
  const isReplace = showReplaceConfirmation && !!pendingAction
  const isConfirmationOpen = showReplaceConfirmation || showRemoveConfirmation

  const handleConfirm = () => {
    if (isReplace && onConfirmReplace) onConfirmReplace()
    else if (showRemoveConfirmation && onConfirmRemove) onConfirmRemove()
  }

  if (apps.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {/* Info message */}
        <div className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-lg">
          <div className="rounded-full bg-muted p-2 shrink-0">
            <Info className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="text-sm text-foreground font-medium">No ELD apps connected</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              You can add trailer or truck numbers manually below, or enable an ELD app for live tracking.
            </p>
          </div>
        </div>

        <EnableELDAppsButton onAppsChanged={refetchELDApps} />
        {!!isConfirmationOpen && !!onCancelConfirmation && (
          <ELDConfirmationModal
            confirmingCurrentValue={confirmingCurrentValue}
            confirmingLabel={confirmingLabel}
            isOpen={isConfirmationOpen}
            isLoadingUpdate={isLoadingUpdate}
            onCancel={onCancelConfirmation}
            onConfirm={handleConfirm}
            pendingValue={pendingAction?.value}
            type={isReplace ? 'replace' : 'remove'}
          />
        )}
        <AssetInputs
          confirmingAssetType={confirmingAssetType}
          currentTrailerNumber={currentTrailerNumber}
          currentTruckNumber={currentTruckNumber}
          isConfirming={isConfirming}
          isLoadingUpdate={isLoadingUpdate}
          onTrailerInputChange={onTrailerInputChange}
          onTrailerRemoveClick={onTrailerRemoveClick}
          onTrailerSubmit={onTrailerSubmit}
          onTruckInputChange={onTruckInputChange}
          onTruckRemoveClick={onTruckRemoveClick}
          onTruckSubmit={onTruckSubmit}
          trailerInputValue={trailerInputValue}
          truckInputValue={truckInputValue}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {!!isConfirmationOpen && !!onCancelConfirmation && (
        <ELDConfirmationModal
          confirmingCurrentValue={confirmingCurrentValue}
          confirmingLabel={confirmingLabel}
          isOpen={isConfirmationOpen}
          isLoadingUpdate={isLoadingUpdate}
          onCancel={onCancelConfirmation}
          onConfirm={handleConfirm}
          pendingValue={pendingAction?.value}
          type={isReplace ? 'replace' : 'remove'}
        />
      )}

      <AssetInputs
        confirmingAssetType={confirmingAssetType}
        currentTrailerNumber={currentTrailerNumber}
        currentTruckNumber={currentTruckNumber}
        isConfirming={isConfirming}
        isLoadingUpdate={isLoadingUpdate}
        onTrailerInputChange={onTrailerInputChange}
        onTrailerRemoveClick={onTrailerRemoveClick}
        onTrailerSubmit={onTrailerSubmit}
        onTruckInputChange={onTruckInputChange}
        onTruckRemoveClick={onTruckRemoveClick}
        onTruckSubmit={onTruckSubmit}
        trailerInputValue={trailerInputValue}
        truckInputValue={truckInputValue}
      />

      {/* ELD Apps Section Header */}
      <div className="flex items-center gap-2 mt-2">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">Or select from ELD app</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* ELD App List - Scrollable */}
      <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto pr-1">
        {apps.map((app) => (
          <ELDAppItem
            key={`${app.appType}-${app.workspaceID}`}
            app={app}
            onSelect={onAppSelect}
            disabled={isConfirming}
          />
        ))}
      </div>

      {/* Enable more ELD apps button - Fixed Footer */}
      <EnableELDAppsButton onAppsChanged={refetchELDApps} />
    </div>
  )
}
